import React, { useEffect, useState } from 'react';
import { Trophy, Coins, Loader2, Flame, Target, Zap, Globe, MapPin, Shield } from 'lucide-react';
import { api } from '../lib/api';
import { BRAND } from '../brand';
import { Avatar } from './Avatar';
import { tierForRating } from '../lib/rating';

interface RatingRow {
  id: string;
  username: string;
  display_name: string;
  avatar_json: string;
  d_rating: number;
  wins: number;
  total_games: number;
  total_answers: number;
  correct_answers: number;
  best_streak: number;
}

interface DahuRow {
  id: string;
  username: string;
  display_name: string;
  avatar_json: string;
  d_rating: number;
  dahus: number;
}

interface EfficiencyRow {
  id: string;
  username: string;
  display_name: string;
  avatar_json: string;
  d_rating: number;
  wins: number;
  total_games: number;
  /** 0..100: acierto × velocidad, promediado por pregunta vista. */
  efficiency: number;
  eff_questions: number;
  accuracy_pct: number;
  avg_response_ms: number;
}

type Row = RatingRow | DahuRow | EfficiencyRow;

type Scope = 'rating' | 'dahus' | 'efficiency';

/** De quién es la clasificación: todo el mundo, un país o una liga. */
type Filter = 'global' | 'country' | 'league';

interface Country {
  code: string;
  name: string;
  count: number;
}

interface MyLeague {
  id: string;
  name: string;
  members: number;
}

const parseAvatar = (json: string) => {
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
};

export const LeaderboardView: React.FC<{
  myId: string;
  onOpenUser: (id: string) => void;
  /** País de quien mira, para proponerlo al elegir «País». */
  myCountry?: string | null;
  /** Llega al abrir la clasificación desde una liga. */
  initialLeague?: string | null;
  onOpenLeagues?: () => void;
}> = ({ myId, onOpenUser, myCountry, initialLeague, onOpenLeagues }) => {
  const [scope, setScope] = useState<Scope>('rating');
  const [filter, setFilter] = useState<Filter>(initialLeague ? 'league' : 'global');
  const [country, setCountry] = useState(myCountry ?? '');
  const [league, setLeague] = useState(initialLeague ?? '');
  const [countries, setCountries] = useState<Country[] | null>(null);
  const [leagues, setLeagues] = useState<MyLeague[] | null>(null);

  useEffect(() => {
    void api
      .get<{ countries: Country[] }>('/leaderboard/countries')
      .then((r) => {
        setCountries(r.countries);
        setCountry((prev) => prev || r.countries[0]?.code || '');
      })
      .catch(() => setCountries([]));
    if (!myId) {
      setLeagues([]);
      return;
    }
    void api
      .get<{ leagues: MyLeague[] }>('/leagues/mine')
      .then((r) => {
        setLeagues(r.leagues);
        setLeague((prev) => prev || r.leagues[0]?.id || '');
      })
      .catch(() => setLeagues([]));
  }, [myId]);

  // La consulta que corresponde al ranking y al ámbito elegidos; null si el
  // ámbito aún no tiene con qué filtrar (sin país o sin liga).
  const query =
    filter === 'global'
      ? `scope=${scope}`
      : filter === 'country'
        ? country
          ? `scope=${scope}&filter=country&country=${encodeURIComponent(country)}`
          : null
        : league
          ? `scope=${scope}&filter=league&league=${encodeURIComponent(league)}`
          : null;
  const dataKey = query ?? `none-${filter}`;
  // Las filas se guardan junto al ranking del que vienen. Al cambiar de
  // pestaña hay un render con el scope nuevo y las filas viejas todavía
  // puestas; pintar filas de D-Rating como si fueran de Dahus leía un
  // `dahus` inexistente y tumbaba la página entera.
  const [data, setData] = useState<{ key: string; rows: Row[] } | null>(null);
  const loading = query !== null && data?.key !== dataKey;
  const rows = data && !loading && data.key === dataKey ? data.rows : [];

  useEffect(() => {
    if (query === null) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<{ leaderboard: Row[] }>(`/leaderboard?${query}`);
        if (!cancelled) setData({ key: query, rows: res.leaderboard });
      } catch {
        if (!cancelled) setData({ key: query, rows: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [query]);

  const selectClass =
    'h-10 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-200 text-sm outline-none focus:border-sky-500 max-w-full';

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div className="text-center space-y-2">
        <Trophy className="w-10 h-10 mx-auto text-ember-400" />
        <h1 className="text-3xl font-display font-black">Clasificación</h1>
      </div>

      {/* Dos rankings distintos: habilidad y acumulación no son lo mismo,
          y mezclarlos premiaría a quien más juega por encima de quien mejor juega. */}
      <div className="flex justify-center gap-2">
        <ScopeTab active={scope === 'rating'} onClick={() => setScope('rating')} icon={<Target className="w-4 h-4" />} label="D-Rating" />
        <ScopeTab active={scope === 'efficiency'} onClick={() => setScope('efficiency')} icon={<Zap className="w-4 h-4" />} label="Eficiencia" />
        <ScopeTab active={scope === 'dahus'} onClick={() => setScope('dahus')} icon={<Coins className="w-4 h-4" />} label={BRAND.currencyName} />
      </div>

      {/* Ámbito */}
      <div className="flex flex-col items-center gap-3">
        <div className="inline-flex p-1 rounded-xl bg-ink-850 border border-ink-700" role="tablist" aria-label="Ámbito de la clasificación">
          <FilterTab active={filter === 'global'} onClick={() => setFilter('global')} icon={<Globe className="w-4 h-4" />} label="Global" />
          <FilterTab active={filter === 'country'} onClick={() => setFilter('country')} icon={<MapPin className="w-4 h-4" />} label="País" />
          <FilterTab active={filter === 'league'} onClick={() => setFilter('league')} icon={<Shield className="w-4 h-4" />} label="Liga" />
        </div>

        {filter === 'country' && countries && countries.length > 0 && (
          <select value={country} onChange={(e) => setCountry(e.target.value)} aria-label="País" className={selectClass}>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name} ({c.count})
              </option>
            ))}
          </select>
        )}

        {filter === 'league' && leagues && leagues.length > 0 && (
          <select value={league} onChange={(e) => setLeague(e.target.value)} aria-label="Liga" className={selectClass}>
            {leagues.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.members})
              </option>
            ))}
          </select>
        )}
      </div>

      {scope === 'efficiency' && (
        <p className="text-center text-ink-400 text-sm max-w-xl mx-auto">
          Una nota de 0 a 100 que mezcla aciertos y velocidad: 100 sería acertar
          todas al instante. Contestar bien y rápido suma más; fallar o no
          contestar cuenta como 0. Se entra con 20 preguntas jugadas.
        </p>
      )}

      {filter === 'league' && leagues !== null && leagues.length === 0 ? (
        <div className="surface p-10 text-center space-y-3">
          <p className="text-ink-400 text-sm">
            {myId ? 'Aún no perteneces a ninguna liga.' : 'Entra en tu cuenta para ver la clasificación de tus ligas.'}
          </p>
          {myId && onOpenLeagues && (
            <button onClick={onOpenLeagues} className="text-sky-400 hover:text-sky-300 font-medium text-sm">
              Ver ligas
            </button>
          )}
        </div>
      ) : filter === 'country' && countries !== null && countries.length === 0 ? (
        <div className="surface p-10 text-center text-ink-400 text-sm">Nadie ha indicado su país todavía.</div>
      ) : query === null || loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      ) : rows.length === 0 ? (
        <div className="surface p-12 text-center text-ink-400">
          {scope === 'efficiency'
            ? 'Nadie ha jugado aún las 20 preguntas necesarias para entrar.'
            : 'Todavía no hay suficientes partidas jugadas.'}
        </div>
      ) : (
        <ol className="space-y-2">
          {rows.map((row, i) => {
            const isMe = row.id === myId;
            const rating = row.d_rating;
            const tier = tierForRating(rating);

            return (
              <li
                key={row.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                  isMe
                    ? 'bg-sky-500/12 border-sky-600'
                    : 'bg-ink-900/70 border-ink-800 hover:border-ink-700'
                }`}
              >
                <span
                  className={`w-8 text-center font-display font-black text-lg ${
                    i === 0
                      ? 'text-ember-400'
                      : i === 1
                        ? 'text-ink-300'
                        : i === 2
                          ? 'text-ember-700'
                          : 'text-ink-600'
                  }`}
                >
                  {i + 1}
                </span>

                <button
                  onClick={() => onOpenUser(row.id)}
                  className="shrink-0 rounded-full"
                  aria-label={`Ver el perfil de ${row.display_name}`}
                >
                  <Avatar config={parseAvatar(row.avatar_json)} size={40} />
                </button>

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink-50 truncate">
                    <button
                      onClick={() => onOpenUser(row.id)}
                      className="hover:text-sky-400 hover:underline underline-offset-2 transition-colors"
                    >
                      {row.display_name}
                    </button>
                    {isMe && <span className="text-sky-400 text-xs ml-2">tú</span>}
                  </p>
                  <p className="text-ink-500 text-xs">
                    {tier.icon} {tier.label}
                    {'efficiency' in row ? (
                      <>
                        {' '}· {row.accuracy_pct}% acierto · {(row.avg_response_ms / 1000).toFixed(1)} s de media
                      </>
                    ) : 'total_games' in row && row.total_games > 0 && (
                      <> · {row.wins} {row.wins === 1 ? 'victoria' : 'victorias'} de {row.total_games}</>
                    )}
                  </p>
                </div>

                {'best_streak' in row && row.best_streak > 0 && (
                  <span className="hidden sm:flex chip border-ink-700 bg-ink-850 text-ink-400">
                    <Flame className="w-3 h-3" /> {row.best_streak}
                  </span>
                )}

                <span className="font-mono tabular-nums font-bold text-lg shrink-0">
                  {'efficiency' in row ? (
                    // Es una nota sobre 100: sin la escala al lado, «57,1» no
                    // decía si eran puntos, segundos o un porcentaje.
                    <span className="flex flex-col items-end leading-none">
                      <span className="text-sky-400">
                        {Number(row.efficiency ?? 0).toLocaleString('es', { minimumFractionDigits: 1 })}
                        <span className="text-xs font-sans font-medium text-ink-500"> /100</span>
                      </span>
                      <span className="text-[0.625rem] font-sans font-medium uppercase tracking-wider text-ink-500 mt-1">
                        eficiencia
                      </span>
                    </span>
                  ) : scope === 'dahus' ? (
                    <span className="text-ember-400">
                      {Number((row as DahuRow).dahus ?? 0).toLocaleString('es')}
                    </span>
                  ) : (
                    <span className="text-ink-100">{Number(rating ?? 0).toLocaleString('es')}</span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

const FilterTab: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`flex items-center gap-1.5 px-4 h-9 rounded-lg text-sm font-semibold transition-colors ${
      active ? 'bg-ink-700 text-ink-50' : 'text-ink-400 hover:text-ink-100'
    }`}
  >
    {icon}
    {label}
  </button>
);

const ScopeTab: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm border transition-colors ${
      active
        ? 'bg-ink-800 border-ink-600 text-ink-50'
        : 'bg-transparent border-ink-800 text-ink-400 hover:text-ink-200'
    }`}
  >
    {icon}
    {label}
  </button>
);
